// @ts-strict-ignore
import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Text from 'antd/lib/typography/Text';
import Flex from 'antd/lib/flex';
import Icon, { WarningFilled } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { runwayColors } from '../styles/globalStyles';
import { AppComponentProps } from '../../typescriptHelpers';
import { createSettingsMenuRoute, createAppSettingsRoute } from '../../helpers/routeHelpers';
import { userIsRunwayAdmin } from '../../helpers';
import { RootState } from '../../reducers/reducers';
import {
    selectAppSettings,
    selectHasStepsMissingRequiredIntegrations,
    selectIsTriageExcludedForOrg,
    selectWorkflowEnabledSteps,
} from '../../selectors/selectors';
import { getConnectionErrorIntegration, getInvalidIntegration } from '../../helpers/integrationHelpers';
import { getSeenNewAutomations, getSeenUpdatedAutomations } from '../../helpers/localStorageHelper';
import { settingsMenuItems } from '../appSettings/appSettingsHelpers';
import { useApp } from '@/utils/hooks/helpers';
import { useSidebarColors } from '../styles/themeMode';
import { SettingType } from '@/types';

interface SettingsMenuItemProps {
    route: string;
    isActive: boolean;
    isLast: boolean;
    showWarning: boolean;
    showNewDot: boolean;
    label: string;
    icon: React.ComponentType;
}

const SettingsMenuItem: React.FC<SettingsMenuItemProps> = ({
    route,
    isActive,
    isLast,
    showWarning,
    showNewDot,
    label,
    icon,
}) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const sc = useSidebarColors();
    const backgroundColor =
        isActive ? sc.menuActiveBg
        : isHovered ? sc.menuHoverBg
        : 'transparent';

    return (
        <NavLink to={route} style={{ textDecoration: 'none' }}>
            <div
                style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '12px 19px',
                    backgroundColor,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    borderTop: `1px solid ${sc.border}`,
                    borderBottom: isLast ? `1px solid ${sc.border}` : 'none',
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {(showWarning || showNewDot) && (
                    <div style={{ position: 'absolute', left: 16 }}>
                        {showWarning && (
                            <WarningFilled
                                style={{
                                    color: runwayColors.statusYellow,
                                    fontSize: 14,
                                }}
                            />
                        )}
                        {showNewDot && (
                            <div
                                style={{
                                    width: 8,
                                    height: 8,
                                    backgroundColor: runwayColors.primary,
                                    borderRadius: '50%',
                                }}
                            />
                        )}
                    </div>
                )}
                <Flex align="center" gap={20}>
                    <Text
                        style={{
                            color: sc.text,
                            fontWeight: isActive ? 600 : 400,
                            fontSize: 14,
                        }}
                    >
                        {label}
                    </Text>
                    <Icon
                        component={icon}
                        style={{
                            fontSize: 16,
                            color: sc.icon,
                        }}
                    />
                </Flex>
            </div>
        </NavLink>
    );
};

const AppSettingsNavigationContent: React.FC<AppComponentProps> = ({ appId, orgId }) => {
    const appData = useApp({ appId, orgId });
    const platform = appData?.appSettings?.platform;

    const appSettings = useSelector((state: RootState) => selectAppSettings(state, appId));

    const seenNewAutomations = getSeenNewAutomations();
    const seenUpdatedAutomations = getSeenUpdatedAutomations();

    const hasNewAutomation =
        appSettings?.automations?.some(
            (automation) =>
                (automation.isNew && !seenNewAutomations?.[automation.type]) ||
                (automation.isUpdated && !seenUpdatedAutomations?.[automation.type]),
        ) ?? false;

    const { pathname } = useLocation();

    const needsTag =
        appSettings?.integrations ?
            getInvalidIntegration(appSettings) || getConnectionErrorIntegration(appSettings)
        :   false;

    const isRunwayAdmin = useSelector((state: RootState) => userIsRunwayAdmin(state.user));
    const isTriageExcluded = useSelector(selectIsTriageExcludedForOrg);

    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));

    const hasStepsMissingRequiredIntegrations = useSelector((state: RootState) =>
        selectHasStepsMissingRequiredIntegrations(state, appId),
    );

    const menuItems = settingsMenuItems.filter((item) => {
        if (item.showOnlyForWhiteLabelApps && !appData?.appSettings?.isWhiteLabel) {
            return false;
        }

        if (item.minimumRequiredMigratedWorkflowStepsToShow) {
            return item.minimumRequiredMigratedWorkflowStepsToShow.every((step) => workflowEnabledSteps?.[step]);
        }

        if (item.minimumRequiredMigratedWorkflowStepsToHide) {
            return !item.minimumRequiredMigratedWorkflowStepsToHide.some((step) => workflowEnabledSteps?.[step]);
        }

        if (item.isRunwayAdminRouteOnly) {
            return isRunwayAdmin;
        }

        if (item.hideForTriageExcludedOrgs) {
            return !isTriageExcluded;
        }

        return true;
    });

    const baseAppSettingsRoute = createAppSettingsRoute(orgId, appId);
    const isOnBaseSettingsRoute = pathname === baseAppSettingsRoute;

    return (
        <div style={{ marginTop: 10 }}>
            {menuItems.map((item, index) => {
                const route = createSettingsMenuRoute(orgId, appId, item.path);
                const subroute = item.subroute?.(orgId, appId);
                const isActive =
                    pathname === route ||
                    pathname === subroute ||
                    (item.id === SettingType.General && isOnBaseSettingsRoute);

                const hideSetting = item.showOnlyOnPlatforms ? !item.showOnlyOnPlatforms.includes(platform) : false;

                if (hideSetting) return null;

                const showWarning =
                    (needsTag && item.id === SettingType.Integrations) ||
                    (hasStepsMissingRequiredIntegrations && item.id === SettingType.WorkflowManagement);
                const showNewDot = hasNewAutomation && item.id === SettingType.Automations;

                return (
                    <SettingsMenuItem
                        key={route}
                        route={route}
                        isActive={isActive}
                        isLast={index === menuItems.length - 1}
                        showWarning={showWarning}
                        showNewDot={showNewDot}
                        label={item.label}
                        icon={item.icon}
                    />
                );
            })}
        </div>
    );
};

export default AppSettingsNavigationContent;
